import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { collection, getDocs, query, where, limit } from 'firebase/firestore';
import { db } from '../api/firebase';
import Fuse from 'fuse.js';
import {
  Typography,
  CircularProgress,
  Alert,
  Box,
  TextField,
  InputAdornment,
  Container,
  Autocomplete,
  Chip,
  Paper,
  Button,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import InstagramIcon from '@mui/icons-material/Instagram';
import SchoolIcon from '@mui/icons-material/School';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import CollegeCarousel from '../components/CollegeCarousel';

const HomePage = () => {
  const [searchResults, setSearchResults] = useState([]);
  const [departments, setDepartments] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [psuUniversityId, setPsuUniversityId] = useState(null);
  const { t } = useLanguage();
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const searchTimeoutRef = useRef(null);

  const MAX_SEARCH_RESULTS = 10;
  const SEARCH_DEBOUNCE_MS = 300;
  const QUERY_LIMIT = 100;

  useEffect(() => {
    const fetchInitialData = async () => {
      setLoading(true);
      setError(null);
      try {
        const universitiesCollectionRef = collection(db, 'universities');
        const universitiesSnapshot = await getDocs(universitiesCollectionRef);
        const psuUniversity = universitiesSnapshot.docs.find(doc => 
          doc.data().name === 'Prince Sultan University'
        );
        
        if (!psuUniversity) {
          throw new Error('Prince Sultan University not found');
        }
        
        setPsuUniversityId(psuUniversity.id);

        const departmentsCollectionRef = collection(db, 'departments');
        const departmentsSnapshot = await getDocs(departmentsCollectionRef);
        const departmentsMap = {};
        departmentsSnapshot.docs.forEach(doc => {
          departmentsMap[doc.id] = doc.data().name;
        });
        setDepartments(departmentsMap);
      } catch (err) {
        setError('Failed to load data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  const performSearch = useCallback(async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2 || !psuUniversityId) {
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);
    try {
      const trimmedSearch = searchTerm.trim().toLowerCase();
      const lecturersRef = collection(db, 'professors');
      const lecturersQuery = query(
        lecturersRef,
        where('universityId', '==', psuUniversityId),
        limit(QUERY_LIMIT)
      );

      const snapshot = await getDocs(lecturersQuery);
      const allLecturers = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      const matchingLecturers = allLecturers.filter(prof => {
        const name = (prof.name || prof.full_name || '').toLowerCase();
        const nameArabic = (prof.nameArabic || '').toLowerCase();
        const keywords = prof.searchKeywords || [];
        
        return (
          name.includes(trimmedSearch) ||
          nameArabic.includes(trimmedSearch) ||
          keywords.some(keyword => keyword.toLowerCase().includes(trimmedSearch))
        );
      });

      if (matchingLecturers.length > 0) {
        const tempFuse = new Fuse(matchingLecturers, {
          keys: [
            { name: 'name', weight: 2 },
            { name: 'full_name', weight: 2 },
            { name: 'nameArabic', weight: 2 },
            { name: 'searchKeywords', weight: 1.5 }
          ],
          threshold: 0.4,
          includeScore: true,
        });

        const fuzzyResults = tempFuse.search(searchTerm);
        const rankedResults = fuzzyResults
          .slice(0, MAX_SEARCH_RESULTS)
          .map(result => result.item);

        setSearchResults(rankedResults);
      } else {
        setSearchResults([]);
      }

    } catch (err) {
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, [psuUniversityId]);

  const handleLecturerSelect = (event, lecturer) => {
    if (lecturer) {
      setSearchLoading(true);
      navigate(`/lecturer/${lecturer.id}`);
    }
  };

  const handleInputChange = (event, newInputValue) => {
    setInputValue(newInputValue);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (newInputValue && newInputValue.length >= 2) {
      searchTimeoutRef.current = setTimeout(() => {
        performSearch(newInputValue);
      }, SEARCH_DEBOUNCE_MS);
    } else {
      setSearchResults([]);
      setSearchLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ m: 4 }}>{error}</Alert>;
  }

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          textAlign: 'center',
          position: 'relative',
          background: 'radial-gradient(circle at 50% 50%, rgba(59, 130, 246, 0.08) 0%, rgba(255, 255, 255, 0) 50%)',
          mb: 4,
        }}
      >
        <Container maxWidth="md">
          <Chip 
            label={t("Prince Sultan University", "جامعة الأمير سلطان")}
            icon={<SchoolIcon sx={{ fontSize: 16 }} />}
            sx={{ 
              mb: 3, 
              bgcolor: 'primary.50', 
              color: 'primary.main',
              fontWeight: 600,
              border: '1px solid',
              borderColor: 'primary.100'
            }}
          />
          
          <Typography
            variant="h1"
            sx={{
              fontWeight: 800,
              fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4rem' },
              lineHeight: 1.1,
              mb: 2,
              background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              pb: 1, // Prevent descenders being cut off
            }}
          >
            {t('Rate your lecturer', 'قيّم محاضرك')}
          </Typography>
          
          <Typography
            variant="h5"
            color="text.secondary"
            sx={{
              fontWeight: 400,
              mb: 6,
              maxWidth: 600,
              mx: 'auto',
              lineHeight: 1.6,
            }}
          >
            {t(
              'Join thousands of students helping each other make better choices.',
              'انضم إلى آلاف الطلاب الذين يساعدون بعضهم البعض في اتخاذ خيارات أفضل.'
            )}
          </Typography>
          
          {/* Search Box */}
          <Box sx={{ maxWidth: 680, mx: 'auto', position: 'relative', zIndex: 10 }}>
            <Autocomplete
              options={searchResults}
              filterOptions={(x) => x}
              getOptionLabel={(option) => option.name || option.full_name || ''}
              loading={searchLoading}
              onChange={handleLecturerSelect}
              onInputChange={handleInputChange}
              inputValue={inputValue}
              freeSolo
              noOptionsText={
                inputValue.length < 2 
                  ? t("Type at least 2 characters...", "اكتب حرفين على الأقل...")
                  : searchLoading 
                    ? t("Searching...", "جاري البحث...")
                    : (
                      <Box sx={{ textAlign: 'center', py: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          {t("No lecturers found", "لم يتم العثور على محاضرين")}
                        </Typography>
                        {isAdmin && (
                          <Link 
                            to="/add-lecturer" 
                            style={{ 
                              textDecoration: 'none',
                              color: '#1e3a8a',
                              fontWeight: 600,
                              marginTop: 8,
                              display: 'inline-block'
                            }}
                          >
                            {t("Add them to Me'ar →", "أضفهم إلى معيار ←")}
                          </Link>
                        )}
                      </Box>
                    )
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={t('Search for a lecturer...', 'ابحث عن محاضر...')}
                  variant="outlined"
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: 'primary.main', fontSize: 24, ml: 1 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <>
                        {searchLoading && <CircularProgress color="primary" size={20} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                    sx: {
                      bgcolor: 'white',
                      borderRadius: 4,
                      fontSize: '1.1rem',
                      py: 1,
                      pl: 1,
                      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                      '& fieldset': { border: '1px solid', borderColor: 'rgba(0,0,0,0.05)' },
                      '&:hover fieldset': { borderColor: 'primary.light' },
                      '&.Mui-focused fieldset': { borderColor: 'primary.main', borderWidth: 2 },
                    }
                  }}
                />
              )}
              renderOption={(props, option) => {
                const { key, ...otherProps } = props;
                return (
                  <Box component="li" key={key} {...otherProps} sx={{ py: 1.5, px: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          bgcolor: 'primary.50',
                          color: 'primary.main',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <PersonIcon />
                      </Box>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                          {option.name || option.full_name}
                        </Typography>
                        {departments[option.departmentId] && (
                          <Typography variant="caption" color="text.secondary">
                            {departments[option.departmentId]}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                );
              }}
              PaperComponent={({ children, ...other }) => (
                <Paper {...other} sx={{ mt: 2, borderRadius: 4, boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', overflow: 'hidden' }}>
                  {children}
                </Paper>
              )}
            />
          </Box>
        </Container>
      </Box>

      {/* Colleges Section */}
      <Container maxWidth="lg" sx={{ mb: 10 }}>
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            {t('Browse by College', 'تصفح حسب الكلية')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('Explore departments and find your lecturers', 'استكشف الأقسام وابحث عن محاضريك')}
          </Typography>
        </Box>
        
        <CollegeCarousel universityId={psuUniversityId} />
      </Container>

      {/* Info Section */}
      <Container maxWidth="md" sx={{ mb: 8 }}>
        <Paper 
          elevation={0}
          sx={{ 
            p: 6, 
            textAlign: 'center',
            background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)',
            border: '1px solid',
            borderColor: 'grey.200',
            borderRadius: 4
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, color: 'text.primary' }}>
            {t('Why use Me\'ar?', 'لماذا تستخدم معيار؟')}
          </Typography>
          
          <Typography variant="body1" color="text.secondary" paragraph sx={{ maxWidth: 600, mx: 'auto', mb: 4, fontSize: '1.1rem' }}>
            {t(
              'Me\'ar provides a safe, anonymous space for students to share their experiences. Your feedback helps improving the educational quality and assists other students in their academic journey.',
              'يوفر معيار مساحة آمنة ومجهولة للطلاب لمشاركة تجاربهم. تساعد ملاحظاتك في تحسين الجودة التعليمية وتساعد الطلاب الآخرين في رحلتهم الأكاديمية.'
            )}
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'center', gap: 2 }}>
            <Button 
              component={Link} 
              to="/about" 
              variant="contained" 
              color="primary"
              size="large"
              sx={{ px: 4, borderRadius: 3, textTransform: 'none' }}
            >
              {t('Learn more about us', 'تعرف أكثر علينا')}
            </Button>
            
            <Button
              component="a"
              href="https://www.instagram.com/mear.sa/"
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<InstagramIcon />}
              variant="outlined"
              size="large"
              sx={{
                borderRadius: 3,
                textTransform: 'none',
                px: 4,
                borderColor: 'grey.300',
                color: 'text.primary',
                '&:hover': {
                  borderColor: '#E1306C',
                  color: '#E1306C',
                  backgroundColor: 'rgba(225, 48, 108, 0.04)'
                }
              }}
            >
              @mear.sa
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default HomePage;